import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { Plus, AlertTriangle, CalendarDays, CheckCircle2, ChevronRight, ExternalLink } from "lucide-react";
import TaskFormModal from "@/components/TaskFormModal";

function SectionCard({ title, count, icon: Icon, tone = "neutral", children, testid }) {
  const tones = {
    red:     "border-red-200 bg-red-50/40",
    amber:   "border-amber-200 bg-amber-50/40",
    neutral: "border-gray-200 bg-white",
  };
  return (
    <section data-testid={testid} className={`rounded-lg border ${tones[tone]} overflow-hidden`}>
      <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100 bg-white/60">
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className={`w-4 h-4 ${tone === "red" ? "text-red-600" : tone === "amber" ? "text-amber-600" : "text-navy"}`} />}
          <h3 className="text-sm font-semibold text-gray-900 tracking-tight">{title}</h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">{count}</span>
        </div>
      </div>
      <div className="divide-y divide-gray-100">{children}</div>
    </section>
  );
}

function TaskRow({ t, onClick }) {
  return (
    <button
      data-testid={`dashboard-task-${t.id}`}
      onClick={() => onClick(t)}
      className="w-full text-left px-5 py-3.5 hover:bg-gray-50 flex items-center gap-3 transition-colors group"
    >
      <PriorityBadge priority={t.priority} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{t.title}</div>
        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
          <span>{t.project}</span>
          <span>·</span>
          <span>{t.format}</span>
          {t.due_date && (<><span>·</span><span>Due {t.due_date}</span></>)}
        </div>
      </div>
      <StatusBadge status={t.status} />
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-navy" />
    </button>
  );
}

function Empty({ msg }) {
  return <div className="px-5 py-6 text-sm text-gray-400 italic">{msg}</div>;
}

export default function HomeDashboard() {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/dashboard").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning"; if (h < 17) return "Good afternoon"; return "Good evening";
  })();

  const openTask = (t) => nav(`/work-tracker?task=${t.id}`);

  if (!data) return <div className="text-sm text-gray-400">Loading dashboard…</div>;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">{today}</div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 mt-2">
            {greeting}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-sm text-gray-500 mt-1.5">Here's what's on your plate today.</p>
        </div>
        <button
          data-testid="quick-add-task-btn"
          onClick={() => setShowModal(true)}
          className="bg-navy hover:bg-navy-800 text-white px-4 py-2.5 rounded-md font-medium text-sm inline-flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" /> Quick add task
        </button>
      </header>

      {/* Personal sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard testid="overdue-section" title="Overdue" count={data.overdue.length} icon={AlertTriangle} tone="red">
          {data.overdue.length === 0
            ? <Empty msg="Nothing overdue. Nice." />
            : data.overdue.map((t) => <TaskRow key={t.id} t={t} onClick={openTask} />)}
        </SectionCard>

        <SectionCard testid="due-today-section" title="Due today" count={data.due_today.length} icon={CalendarDays}>
          {data.due_today.length === 0
            ? <Empty msg="No tasks due today." />
            : data.due_today.map((t) => <TaskRow key={t.id} t={t} onClick={openTask} />)}
        </SectionCard>

        <SectionCard testid="upcoming-section" title="Upcoming (next 3 days)" count={data.upcoming.length} icon={CheckCircle2}>
          {data.upcoming.length === 0
            ? <Empty msg="Quiet calendar ahead." />
            : data.upcoming.map((t) => <TaskRow key={t.id} t={t} onClick={openTask} />)}
        </SectionCard>
      </div>

      {/* Admin-only panels */}
      {isAdmin && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section data-testid="team-summary-panel" className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 tracking-tight">Team summary</h3>
                <p className="text-xs text-gray-500 mt-0.5">Workload across the team</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50/50 text-[11px] uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="text-left px-5 py-2.5 font-medium">Member</th>
                      <th className="text-right px-3 py-2.5 font-medium">In progress</th>
                      <th className="text-right px-3 py-2.5 font-medium">Overdue</th>
                      <th className="text-right px-5 py-2.5 font-medium">Done · 7d</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.team_summary.map((m) => (
                      <tr key={m.id} data-testid={`team-row-${m.id}`} className="hover:bg-gray-50/50">
                        <td className="px-5 py-3">
                          <div className="font-medium text-gray-900">{m.name}</div>
                          <div className="text-[11px] text-gray-500 uppercase tracking-wider">{m.role === "admin" ? "Admin" : "Team"}</div>
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-gray-900">{m.in_progress}</td>
                        <td className={`px-3 py-3 text-right font-medium ${m.overdue > 0 ? "text-red-600" : "text-gray-900"}`}>{m.overdue}</td>
                        <td className="px-5 py-3 text-right font-medium text-gray-900">{m.completed_week}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section data-testid="project-health-panel" className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 tracking-tight">Project health</h3>
                <p className="text-xs text-gray-500 mt-0.5">Delivered vs total scope</p>
              </div>
              <div className="divide-y divide-gray-100">
                {data.project_health.length === 0
                  ? <Empty msg="No projects yet." />
                  : data.project_health.map((p) => {
                      const dotColor = p.health === "green" ? "bg-emerald-500" : p.health === "amber" ? "bg-amber-500" : "bg-red-500";
                      return (
                        <div key={p.name} className="px-5 py-3 flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                            <div className="text-[11px] uppercase tracking-wider text-gray-500">{p.client}</div>
                          </div>
                          <div className="text-sm font-mono text-gray-900">{p.delivered}/{p.total}</div>
                        </div>
                      );
                    })}
              </div>
            </section>
          </div>

          <SectionCard testid="pending-approvals-section" title="Pending approvals" count={data.pending_approvals?.length || 0} icon={CheckCircle2}>
            {(!data.pending_approvals || data.pending_approvals.length === 0)
              ? <Empty msg="No tasks awaiting your approval." />
              : data.pending_approvals.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`approval-row-${t.id}`}
                    onClick={() => openTask(t)}
                    className="w-full text-left px-5 py-3.5 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <PriorityBadge priority={t.priority} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{t.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{t.project} · By {t.assignee_name}</div>
                    </div>
                    {t.google_doc_link && (
                      <a
                        href={t.google_doc_link} target="_blank" rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-navy hover:underline inline-flex items-center gap-1"
                      >
                        Doc <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <StatusBadge status={t.status} />
                  </button>
                ))}
          </SectionCard>
        </>
      )}

      <TaskFormModal open={showModal} onClose={() => setShowModal(false)} onSaved={load} />
    </div>
  );
}
